'use client';

import type { FirebaseApp } from 'firebase/app';
import type { IAnalyticsService } from '../types/analytics.types';
import { logger } from '@/core/logger';

declare global {
	interface Window {
		clarity?: (action: string, ...args: unknown[]) => void;
	}
}

class ClarityAnalyticsService implements IAnalyticsService {
	private initialized: boolean = false;
	private projectId: string;

	constructor() {
		const projectId = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;

		if (!projectId) {
			logger.warn(
				'Clarity not configured: NEXT_PUBLIC_CLARITY_PROJECT_ID environment variable is missing'
			);
			this.projectId = '';
			return;
		}

		this.projectId = projectId;
	}

	async initialize(app: FirebaseApp): Promise<boolean> {
		// Skip if not configured
		if (!this.projectId) {
			return false;
		}

		// Prevent re-initialization
		if (this.initialized) {
			return true;
		}

		// Check if running in browser
		if (typeof window === 'undefined') {
			return false;
		}

		try {
			// Load Clarity script
			const win = window as unknown as Record<string, unknown>;
			const clarityKey = 'clarity';

			// Initialize clarity function if not exists
			if (!win[clarityKey]) {
				win[clarityKey] = function (...args: unknown[]) {
					const fn = win[clarityKey] as unknown as Record<
						string,
						unknown
					>;
					const queue = (fn.q || []) as unknown[];
					queue.push(args);
					fn.q = queue;
				};
			}

			// Load the Clarity script
			const script = document.createElement('script');
			script.async = true;
			script.src = 'https://www.clarity.ms/tag/' + this.projectId;
			const firstScript = document.getElementsByTagName('script')[0];
			if (firstScript && firstScript.parentNode) {
				firstScript.parentNode.insertBefore(script, firstScript);
			}

			this.initialized = true;
			return true;
		} catch (error) {
			logger.error('Failed to initialize Clarity', error);
			return false;
		}
	}

	identify(
		userId: string,
		userProperties?: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
	): void {
		if (
			!this.initialized ||
			typeof window === 'undefined' ||
			!window.clarity
		) {
			return;
		}

		try {
			window.clarity('identify', userId);
			if (userProperties) {
				Object.entries(userProperties).forEach(([key, value]) => {
					this.setTag(key, String(value));
				});
			}
		} catch (error) {
			logger.error('Failed to identify user in Clarity', error);
		}
	}

	setTag(key: string, value: string | string[]): void {
		if (
			!this.initialized ||
			typeof window === 'undefined' ||
			!window.clarity
		) {
			return;
		}

		try {
			window.clarity('set', key, value);
		} catch (error) {
			logger.error('Failed to set Clarity tag', error);
		}
	}

	logEvent(
		eventName: string,
		eventParameters?: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
	): void {
		if (
			!this.initialized ||
			typeof window === 'undefined' ||
			!window.clarity
		) {
			return;
		}

		try {
			window.clarity('event', eventName);
		} catch (error) {
			logger.error('Failed to track Clarity event', error);
		}
	}

	logError(
		eventName: string,
		eventParameters?: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
	): void {
		if (
			!this.initialized ||
			typeof window === 'undefined' ||
			!window.clarity
		) {
			return;
		}

		try {
			window.clarity('upgrade', eventName);
		} catch (error) {
			logger.error('Failed to upgrade Clarity session', error);
		}
	}
}

export const clarityService = new ClarityAnalyticsService();
